import api from "./api";
import firebase from "firebase";
import "firebase/auth";

// Firebase ID tokens expire after 60 min; refresh well before that so a request
// never goes out with an expired token (the "session expiry window").
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

// Keepalive state is module-scoped: AuthContext invokes the FirebaseService
// factory on every render, so sharing a single timer / interceptor / listener
// here prevents them from stacking up across instances.
let tokenRefreshInterval = null;
let visibilityHandler = null;
let responseInterceptorId = null;
let sessionErrorHandler = null;

function parseConfig(firebaseConfig) {
  if (firebaseConfig == null) return firebaseConfig;
  return typeof firebaseConfig === "string"
    ? JSON.parse(firebaseConfig)
    : firebaseConfig;
}

const FirebaseService = (firebaseConfig) => {
  firebaseConfig = parseConfig(firebaseConfig);
  // Initialize Firebase
  if (firebase.apps.length < 1) {
    firebase.initializeApp(firebaseConfig);
  }

  const auth = firebase.auth();

  const getToken = () => auth.currentUser.getIdToken(true);

  // Force-refresh the ID token and push it onto the API client.
  const updateToken = async (onSuccess, onFailure) => {
    if (!auth.currentUser) return undefined;
    try {
      const token = await auth.currentUser.getIdToken(true);
      api.defaults.headers.common["Authorization"] = token;
      if (typeof onSuccess === "function") onSuccess();
      return token;
    } catch (error) {
      if (typeof onFailure === "function") onFailure(error);
      throw error;
    }
  };

  const handleRefreshFailure = (error) => {
    if (typeof sessionErrorHandler === "function") sessionErrorHandler(error);
  };

  const refreshOnResume = () => {
    if (auth.currentUser) updateToken(null, handleRefreshFailure);
  };

  // Refresh when the app/tab regains focus — covers the case where the device
  // was asleep long enough for the periodic timer to be throttled. Works on web
  // and inside the Capacitor webview without an extra native dependency.
  const setupResumeRefresh = () => {
    if (visibilityHandler) return;
    visibilityHandler = () => {
      if (document.visibilityState === "visible") refreshOnResume();
    };
    document.addEventListener("visibilitychange", visibilityHandler);
  };

  const clearResumeRefresh = () => {
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
  };

  const startTokenRefresh = (onError) => {
    sessionErrorHandler = onError;
    if (tokenRefreshInterval) clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = setInterval(() => {
      if (auth.currentUser) updateToken(null, handleRefreshFailure);
      else stopTokenRefresh();
    }, REFRESH_INTERVAL_MS);
    setupResumeRefresh();
  };

  const stopTokenRefresh = () => {
    sessionErrorHandler = null;
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
      tokenRefreshInterval = null;
    }
    clearResumeRefresh();
  };

  const clearInterceptors = () => {
    if (responseInterceptorId !== null) {
      api.interceptors.response.eject(responseInterceptorId);
      responseInterceptorId = null;
    }
  };

  const isSessionExpiredError = (error) => {
    const status = error?.response?.status;
    const errorCode = error?.response?.data?.code || "";
    const errorString = JSON.stringify(error?.response?.data || {});
    const is401 = status === 401;
    const sessionExpired =
      errorCode === "auth/id-token-expired" ||
      errorCode === "user/id-token-expired";
    const firebaseTokenError =
      errorString.includes("Firebase") && errorString.includes("token");
    return is401 || sessionExpired || firebaseTokenError;
  };

  /**
   * Retries a request once after refreshing the ID token when the API reports an
   * expired / invalid Firebase session, so a token that lapsed between periodic
   * refreshes does not surface a raw 401. If the retry still fails (or there is
   * no user) onSessionExpired is invoked and the original promise is left
   * pending to avoid flashing 401 UX.
   */
  const setupInterceptors = (onSessionExpired) => {
    clearInterceptors();

    responseInterceptorId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (!isSessionExpiredError(error)) {
          return Promise.reject(error);
        }

        const originalConfig = error.config;
        if (originalConfig?.__firebaseAuthRetry) {
          if (typeof onSessionExpired === "function") onSessionExpired(error);
          return new Promise(() => {});
        }
        if (originalConfig) {
          originalConfig.__firebaseAuthRetry = true;
        }

        if (auth.currentUser) {
          try {
            await updateToken();
            return api.request(originalConfig);
          } catch {
            if (typeof onSessionExpired === "function") onSessionExpired(error);
            return new Promise(() => {});
          }
        }

        if (typeof onSessionExpired === "function") onSessionExpired(error);
        return new Promise(() => {});
      }
    );
  };

  return {
    signIn: (email, password) =>
      auth.signInWithEmailAndPassword(email, password).then((user) =>
        getToken().then((token) => {
          api.defaults.headers.common["Authorization"] = token;
          return user;
        })
      ),
    /**
     * @param {function} onSuccess Called with the user once authenticated and the token is set.
     * @param {function} onAuthError Called when there is no user or initial token refresh fails.
     * @param {function} [onApiSessionExpired] Called when the API reports a session error and the
     *   refresh+retry could not recover. Defaults to onAuthError when omitted.
     */
    userLoggedIn: (onSuccess, onAuthError, onApiSessionExpired) => {
      const apiSessionHandler =
        typeof onApiSessionExpired === "function"
          ? onApiSessionExpired
          : onAuthError;

      return auth.onAuthStateChanged((user) => {
        if (user) {
          updateToken(() => {
            setupInterceptors(apiSessionHandler);
            startTokenRefresh(onAuthError);
            if (typeof onSuccess === "function") onSuccess(user);
          }, onAuthError);
        } else {
          stopTokenRefresh();
          clearInterceptors();
          if (typeof onAuthError === "function") onAuthError();
        }
      });
    },
    signOut: () => {
      stopTokenRefresh();
      clearInterceptors();
      return auth.signOut();
    },
    signUp: (correo, password) =>
      auth.createUserWithEmailAndPassword(correo, password),
    recoverPassword: (email) => auth.sendPasswordResetEmail(email),
    getToken: () => auth.currentUser.getIdToken(true),
    refreshToken: (onSuccess) => updateToken(onSuccess),
    verifyEmail: () => auth.currentUser.sendEmailVerification(),
    updateEmail: (email) => auth.currentUser.updateEmail(email),
    setToken: (token) => (api.defaults.headers.common["Authorization"] = token),
    /** @deprecated Interceptors are registered automatically from userLoggedIn. */
    setupInterceptors,
  };
};

export default FirebaseService;
